import { communityStats } from '@/mocks/communityStats';

function formatStatValue(value: number): string {
  if (value >= 1000000) {
    const millions = value / 1000000;
    return millions % 1 === 0 ? `${millions}M` : `${millions.toFixed(1)}M`;
  }
  if (value >= 1000) {
    const thousands = value / 1000;
    return thousands % 1 === 0 ? `${thousands.toLocaleString('es-ES')}` : `${thousands.toFixed(0)}K`;
  }
  return value.toLocaleString('es-ES');
}

const iconColors: Record<string, string> = {
  'ri-radio-line': 'bg-rose-50 text-rose-600 group-hover:bg-rose-500 group-hover:text-white',
  'ri-mic-line': 'bg-amber-50 text-amber-600 group-hover:bg-amber-500 group-hover:text-white',
  'ri-calendar-check-line': 'bg-emerald-50 text-emerald-600 group-hover:bg-emerald-500 group-hover:text-white',
  'ri-headphone-line': 'bg-sky-50 text-sky-600 group-hover:bg-sky-500 group-hover:text-white',
  'ri-calendar-event-line': 'bg-violet-50 text-violet-600 group-hover:bg-violet-500 group-hover:text-white',
  'ri-shield-star-line': 'bg-orange-50 text-orange-600 group-hover:bg-orange-500 group-hover:text-white',
  'ri-chat-3-line': 'bg-cyan-50 text-cyan-600 group-hover:bg-cyan-500 group-hover:text-white',
  'ri-heart-3-line': 'bg-red-50 text-red-600 group-hover:bg-red-500 group-hover:text-white',
  'ri-message-3-line': 'bg-teal-50 text-teal-600 group-hover:bg-teal-500 group-hover:text-white',
  'ri-share-forward-line': 'bg-indigo-50 text-indigo-600 group-hover:bg-indigo-500 group-hover:text-white',
};

export default function CommunityStatsSection() {
  return (
    <section id="community-stats" className="relative py-20 md:py-28 overflow-hidden bg-background-50">
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-accent-500/3 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-[350px] h-[350px] bg-rose-500/3 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-50 border border-accent-200 text-accent-700 text-xs font-semibold mb-5">
            <i className="ri-bar-chart-grouped-line text-xs"></i>
            La Comunidad en Números
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 tracking-tight leading-[1.1]">
            Una comunidad que{' '}
            <span className="text-accent-600">no para de crecer</span>
          </h2>
          <p className="mt-4 text-foreground-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            Cada número representa personas reales: radios que transmiten, locutores que crean, oyentes que participan. Esto es RFW hoy.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
          {communityStats.map((stat) => (
            <div
              key={stat.id}
              className="group relative flex flex-col items-center text-center p-5 md:p-6 rounded-2xl bg-background-50 border border-background-200/60 hover:border-background-300/60 transition-all duration-300 hover:-translate-y-1 hover:shadow-sm cursor-default"
            >
              {/* Icon */}
              <div className={`w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-xl ${iconColors[stat.icon] || 'bg-background-100 text-foreground-600'} transition-all duration-300 mb-3`}>
                <i className={`${stat.icon} text-lg md:text-xl`}></i>
              </div>

              {/* Number */}
              <div className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-foreground-950 mb-1 tracking-tight tabular-nums">
                {formatStatValue(stat.value)}
                {stat.suffix && (
                  <span className="text-accent-500 text-xl md:text-2xl">{stat.suffix}</span>
                )}
              </div>

              {/* Label */}
              <span className="text-xs md:text-sm font-semibold text-foreground-700 mb-1.5 whitespace-nowrap">
                {stat.label}
              </span>

              {/* Description — shows on hover */}
              <div className="absolute inset-0 flex items-center justify-center p-4 rounded-2xl bg-background-50/95 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                <p className="text-xs text-foreground-500 leading-relaxed">
                  {stat.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom note — preparing for real data */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 border border-background-200/70 text-foreground-400 text-xs">
            <i className="ri-database-2-line text-xs"></i>
            Estadísticas simuladas — Preparado para datos reales
          </div>
        </div>
      </div>
    </section>
  );
}